/**
 * 自然语言画布（画布②）的纯逻辑：指令解析与批量拆分。
 * 独立成纯函数模块，便于单元测试。
 */

/** 识别到的指令动作类型（用于给历史列表打标签/图标）。 */
export type NlCommandType =
  | "add" // 增加/新增/添加
  | "modify" // 修改/调整/更改
  | "remove" // 删除/移除
  | "rename" // 重命名/改名为
  | "other";

export interface ParsedInstruction {
  /** 原始指令文本（去除首尾空白） */
  raw: string;
  /** 识别出的动作类型 */
  type: NlCommandType;
  /** 去除动作动词后的描述 */
  description: string;
}

const COMMAND_RULES: { type: NlCommandType; patterns: RegExp[] }[] = [
  {
    type: "add",
    patterns: [/^(增加|新增|添加|加上|加入)/, /^(add|create|append)\b/i],
  },
  {
    type: "remove",
    patterns: [/^(删除|移除|去掉)/, /^(remove|delete|drop)\b/i],
  },
  {
    type: "rename",
    patterns: [/^(重命名|改名为)/, /^(rename)\b/i],
  },
  {
    type: "modify",
    patterns: [/^(修改|调整|更改|把|将|让)/, /^(modify|update|change|set|make)\b/i],
  },
];

/**
 * 把多行批量指令拆分为单条指令列表：
 * 按换行拆分，去除空行与纯注释行（# 或 // 开头）。
 */
export function splitBatchInstructions(input: string): string[] {
  return input
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !/^[#/]/.test(line));
}

/** 解析单条指令：识别动作动词并剥离出描述。 */
export function parseInstruction(raw: string): ParsedInstruction {
  const text = raw.trim();
  if (!text) {
    return { raw: text, type: "other", description: text };
  }
  for (const rule of COMMAND_RULES) {
    for (const pattern of rule.patterns) {
      const match = pattern.exec(text);
      if (match) {
        return {
          raw: text,
          type: rule.type,
          description: text.slice(match[0].length).trim(),
        };
      }
    }
  }
  return { raw: text, type: "other", description: text };
}

/** 批量拆分 + 解析为指令列表（nl-canvas 执行入口使用）。 */
export function parseBatchInstructions(input: string): ParsedInstruction[] {
  return splitBatchInstructions(input).map(parseInstruction);
}

/**
 * 画布② 流式步骤模板：AI 处理一条指令的步骤文本流。
 * 前端不流式调用后端，因此按步骤顺序逐条展示，模拟"分析 → 修改 schema → 完成"。
 */
export interface NlStep {
  id: string;
  label: string;
}

export const NL_STEPS: NlStep[] = [
  { id: "analyze", label: "分析指令" },
  { id: "schema", label: "调用 AI 修改 Schema" },
  { id: "done", label: "完成，画布已热更新" },
];
